import { CircleCheckIcon } from "lucide-react";
import { useAtom } from "jotai";
import { atomFamily, atomWithStorage } from "jotai/utils";
import { ComponentProps } from "react";

import { useRequestBetaConnectorAccessMutation } from "@/apollo/types";
import { PrimaryButton } from "@/components/elements/Button";
import {
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalFooter,
  ModalHeader,
} from "@/components/elements/Modal";
import { ConnectorOption } from "@/features/connectors";
import { useMountEffect } from "@/hooks/useMountEffect";
import { IntegrationLogoBox } from "@/integrations";
import { useMixpanel } from "@/monitoring/mixpanel";
import { useToast } from "@/providers/ToastProvider";
import { useCurrentAccount } from "@/providers/account";
import { createStorageKey } from "@/utils/storage";
import { Dialog } from "@headlessui/react";
import * as Sentry from "@sentry/react";

export function SoonConnectorAccessRequestDialog({
  option,
  ...dialogProps
}: {
  option: ConnectorOption;
} & ComponentProps<typeof Modal>) {
  const name = option.label;
  const mp = useMixpanel();

  useMountEffect(() => {
    mp.track("Beta Connector Dialog Opened", {
      connector: name,
    });
  });

  const { requested, handleRequestAccess, loading } = useRequestBetaConnector(
    option.integration.id,
  );

  return (
    <Modal {...dialogProps}>
      <ModalHeader className="flex items-center justify-start gap-4">
        <IntegrationLogoBox id={option.value} size="md" />
        <Dialog.Title className="text-lg">{name} is Coming Soon</Dialog.Title>
      </ModalHeader>
      <ModalCloseButton />
      <ModalBody className="flex flex-col gap-4">
        <p className="text-sm">
          The {name} connector is on our roadmap for upcoming connector releases
          and is not available yet. We can notify you as soon as it’s released.
        </p>
      </ModalBody>
      <ModalFooter>
        {requested ? (
          <div className="flex items-center space-x-4 rounded-sm border p-4 dark:border-gray-700">
            <CircleCheckIcon className="size-6 flex-none text-green-500" />
            <div className="text-xs">
              We will notify you when the {name} connector is released. Thank
              you for your interest!
            </div>
          </div>
        ) : (
          <PrimaryButton
            size="sm"
            onClick={() => {
              handleRequestAccess();
            }}
            className="self-start"
            isLoading={loading}
          >
            Notify me when released
          </PrimaryButton>
        )}
      </ModalFooter>
    </Modal>
  );
}

const requstedBetaConnectorsAtom = atomFamily((accountId: string) =>
  atomWithStorage<{ [integrationId: string]: boolean }>(
    createStorageKey("requested-beta-connector-access", accountId),
    {},
  ),
);

const useRequestedBetaConnectors = () => {
  const { id } = useCurrentAccount();
  return useAtom(requstedBetaConnectorsAtom(id));
};

const useRequestBetaConnector = (integrationId: string) => {
  const toast = useToast();

  const [requested, setRequested] = useRequestedBetaConnectors();

  const [requestBetaConnectorAccess, { loading }] =
    useRequestBetaConnectorAccessMutation();

  const handleRequestBetaConnector = () => {
    if (requested[integrationId]) return;
    if (loading) return;

    requestBetaConnectorAccess({
      variables: {
        integrationId,
      },
      onCompleted() {
        setRequested((prev) => ({
          ...prev,
          [integrationId]: true,
        }));
      },
      onError(e) {
        Sentry.captureException(e);
        toast("Failed to request access", "Please try again later.", "error");
      },
    });
  };
  return {
    handleRequestAccess: handleRequestBetaConnector,
    loading,
    requested: requested[integrationId] || false,
  };
};
